import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useParams } from "react-router-dom";
import { Loader2, CheckCircle2, Star } from "lucide-react";
import { encuestasApi } from "../../lib/api";
import { aplicarFaviconPagina, aplicarTituloPagina } from "../../lib/branding";

interface PreguntaPublica {
  id: number;
  tipo: "texto" | "seleccion" | "rating";
  texto: string;
  requerida: boolean;
  opciones?: Array<{ id: number; texto: string }>;
}
interface BrandingSucursal {
  logoUrl?: string | null;
  imagenFondoUrl?: string | null;
  colorPrimario?: string | null;
  colorSecundario?: string | null;
  sucursalNombre?: string | null;
}
interface EncuestaPublicaQr {
  titulo: string;
  descripcion?: string | null;
  preguntas: PreguntaPublica[];
  branding?: BrandingSucursal | null;
}

const ACCENT_DEFAULT = "#6B9F2F";

export default function ResponderEncuestaQrPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<EncuestaPublicaQr | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [nombreCliente, setNombreCliente] = useState("");
  const [cif, setCif] = useState("");
  const [telefono, setTelefono] = useState("");
  const [mostrarRedes, setMostrarRedes] = useState(false);
  const [instagram, setInstagram] = useState("");
  const [twitter, setTwitter] = useState("");
  const [facebook, setFacebook] = useState("");
  const [tiktok, setTiktok] = useState("");

  const [respuestas, setRespuestas] = useState<Record<number, { opcionId?: number; texto?: string }>>({});
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  useEffect(() => {
    if (!token) return;
    encuestasApi.obtenerPorTokenQr(token)
      .then((d) => {
        setData(d);
        aplicarFaviconPagina(d?.branding?.logoUrl);
        aplicarTituloPagina(d?.branding?.sucursalNombre ?? d?.titulo);
      })
      .catch((e) => setError(e.message || "Enlace inválido"))
      .finally(() => setCargando(false));
  }, [token]);

  // ── Lookup automático de cliente existente por cédula o teléfono ──
  // Evita duplicar clientes en Cliente 360: si ya respondió otra encuesta o
  // tiene reservas/compras previas, se autocompletan sus datos.
  const [clienteAutocompletado, setClienteAutocompletado] = useState<string | null>(null);
  const lookupTimer = useRef<ReturnType<typeof setTimeout>>();
  const lastLookupKey = useRef("");

  useEffect(() => {
    if (!token) return;
    clearTimeout(lookupTimer.current);
    lookupTimer.current = setTimeout(() => {
      const telClean = telefono.replace(/[\s\-()]/g, "");
      const cifClean = cif.trim().toUpperCase();
      const key = `${telClean}|${cifClean}`;
      if (key === lastLookupKey.current) return;
      lastLookupKey.current = key;

      const tieneTel = telClean.length >= 7;
      const tieneCif = cifClean.length >= 5;
      if (!tieneTel && !tieneCif) { setClienteAutocompletado(null); return; }

      encuestasApi.lookupClienteQr(token, { telefono: tieneTel ? telClean : undefined, cif: tieneCif ? cifClean : undefined })
        .then((r) => {
          if (!r) { setClienteAutocompletado(null); return; }
          if (r.nombreCliente) setNombreCliente(r.nombreCliente);
          if (r.telefono) setTelefono(r.telefono);
          if (r.cif) setCif(r.cif);
          if (r.instagram) { setInstagram(r.instagram); setMostrarRedes(true); }
          if (r.twitter) { setTwitter(r.twitter); setMostrarRedes(true); }
          if (r.facebook) { setFacebook(r.facebook); setMostrarRedes(true); }
          if (r.tiktok) { setTiktok(r.tiktok); setMostrarRedes(true); }
          lastLookupKey.current = `${(r.telefono ?? telClean).replace(/[\s\-()]/g, "")}|${(r.cif ?? cifClean).trim().toUpperCase()}`;
          setClienteAutocompletado(r.nombreCliente);
          setTimeout(() => setClienteAutocompletado(null), 4000);
        })
        .catch(() => {});
    }, 600);
    return () => clearTimeout(lookupTimer.current);
  }, [token, telefono, cif]);

  async function enviar() {
    setError(null);
    if (!nombreCliente.trim()) {
      setError("Ingresá tu nombre para continuar.");
      return;
    }
    if (!cif.trim() && !telefono.trim()) {
      setError("Ingresá tu cédula o tu teléfono para continuar.");
      return;
    }
    const faltantes = (data?.preguntas ?? []).filter((p) => {
      if (!p.requerida) return false;
      const r = respuestas[p.id];
      return p.tipo === "texto" ? !r?.texto?.trim() : !r?.opcionId;
    });
    if (faltantes.length > 0) {
      setError("Por favor respondé todas las preguntas obligatorias.");
      return;
    }
    setEnviando(true);
    try {
      await encuestasApi.responderQr(token!, {
        nombreCliente: nombreCliente.trim(),
        cif: cif.trim() || undefined,
        telefono: telefono.trim() || undefined,
        instagram: instagram.trim() || undefined,
        twitter: twitter.trim() || undefined,
        facebook: facebook.trim() || undefined,
        tiktok: tiktok.trim() || undefined,
        respuestas: Object.entries(respuestas).map(([preguntaId, v]) => ({
          preguntaId: Number(preguntaId),
          opcionId: v.opcionId,
          texto: v.texto,
        })),
      });
      setEnviado(true);
    } catch (e: any) {
      setError(e.message || "No pudimos enviar tu respuesta");
    } finally {
      setEnviando(false);
    }
  }

  const accent = data?.branding?.colorPrimario || ACCENT_DEFAULT;
  const fondoEstilo: CSSProperties = data?.branding?.imagenFondoUrl
    ? {
        backgroundImage: `linear-gradient(rgba(246,241,231,0.88), rgba(246,241,231,0.88)), url(${data.branding.imagenFondoUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : {};

  if (cargando) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="size-6 animate-spin text-brand-500" /></div>;
  }

  if (error && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-ink-50">
        <div className="card max-w-sm w-full p-6 text-center">
          <p className="text-sm text-ink-600">{error}</p>
        </div>
      </div>
    );
  }

  if (enviado) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-ink-50" style={fondoEstilo}>
        <div className="card max-w-sm w-full p-8 text-center">
          <CheckCircle2 className="size-12 text-emerald-500 mx-auto mb-3" />
          <h1 className="font-display font-bold text-lg text-ink-800 mb-1">¡Gracias!</h1>
          <p className="text-sm text-ink-500">Tu respuesta quedó registrada.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink-50 py-10 px-4" style={fondoEstilo}>
      <div className="max-w-lg mx-auto card p-6 space-y-5">
        <header className="text-center">
          {data?.branding?.logoUrl ? (
            <img src={data.branding.logoUrl} alt="" className="size-14 rounded-full object-cover mx-auto mb-2 border-2" style={{ borderColor: accent }} />
          ) : (
            <Star className="size-8 mx-auto mb-2" style={{ color: accent }} />
          )}
          <h1 className="font-display font-bold text-xl text-ink-800">{data?.titulo}</h1>
          {data?.descripcion && <p className="text-sm text-ink-500 mt-1">{data.descripcion}</p>}
        </header>

        {error && <div className="px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}

        {/* Bloque de identidad: fijo, siempre primero, no configurable por el gerente */}
        <div className="space-y-3 border border-ink-200 rounded-lg p-4 bg-ink-50/50">
          <p className="text-xs font-semibold text-ink-500 uppercase tracking-wide">Antes de empezar</p>
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">Nombre completo <span className="text-red-500">*</span></label>
            <input className="input w-full" value={nombreCliente} onChange={(e) => setNombreCliente(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">Cédula</label>
              <input className="input w-full" value={cif} onChange={(e) => setCif(e.target.value)} placeholder="V12345678" />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">Teléfono</label>
              <input className="input w-full" value={telefono} onChange={(e) => setTelefono(e.target.value)} placeholder="04141234567" />
            </div>
          </div>
          <p className="text-xs text-ink-400">Necesitamos al menos una de las dos para no duplicar tu registro.</p>
          {clienteAutocompletado && (
            <p className="text-xs font-medium text-emerald-600">
              Te identificamos como {clienteAutocompletado} — completamos tus datos automáticamente.
            </p>
          )}

          {!mostrarRedes ? (
            <button type="button" onClick={() => setMostrarRedes(true)} className="text-xs font-medium hover:underline" style={{ color: accent }}>
              + Agregar mis redes sociales (opcional)
            </button>
          ) : (
            <div className="grid grid-cols-2 gap-3 pt-1">
              <input className="input text-sm" placeholder="Instagram @usuario" value={instagram} onChange={(e) => setInstagram(e.target.value)} />
              <input className="input text-sm" placeholder="X @usuario" value={twitter} onChange={(e) => setTwitter(e.target.value)} />
              <input className="input text-sm" placeholder="Facebook" value={facebook} onChange={(e) => setFacebook(e.target.value)} />
              <input className="input text-sm" placeholder="TikTok @usuario" value={tiktok} onChange={(e) => setTiktok(e.target.value)} />
            </div>
          )}
        </div>

        <div className="space-y-4">
          {data?.preguntas?.map((p, idx) => (
            <div key={p.id}>
              <label className="block text-sm font-medium text-ink-700 mb-1.5">
                {idx + 1}. {p.texto} {p.requerida && <span className="text-red-500">*</span>}
              </label>
              {p.tipo === "texto" ? (
                <textarea className="input w-full text-base" rows={6} style={{ minHeight: "9rem" }}
                  value={respuestas[p.id]?.texto ?? ""}
                  onChange={(e) => setRespuestas((r) => ({ ...r, [p.id]: { texto: e.target.value } }))} />
              ) : p.tipo === "rating" ? (
                <div className="flex flex-wrap gap-2">
                  {p.opciones?.map((o, i) => {
                    const sel = respuestas[p.id]?.opcionId === o.id;
                    return (
                      <label key={o.id}
                        className="flex-1 min-w-[5.5rem] flex flex-col items-center gap-1 px-2 py-2.5 rounded-lg border cursor-pointer text-xs font-medium text-center transition-colors border-ink-200 hover:bg-ink-50 text-ink-600"
                        style={sel ? { borderColor: accent, backgroundColor: `${accent}1a`, color: accent } : undefined}>
                        <input type="radio" name={`pregunta-${p.id}`} className="sr-only"
                          checked={sel}
                          onChange={() => setRespuestas((r) => ({ ...r, [p.id]: { opcionId: o.id } }))} />
                        <span className="text-base leading-none">{i + 1}</span>
                        {o.texto}
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-1.5">
                  {p.opciones?.map((o) => {
                    const sel = respuestas[p.id]?.opcionId === o.id;
                    return (
                      <label key={o.id}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors border-ink-200 hover:bg-ink-50"
                        style={sel ? { borderColor: accent, backgroundColor: `${accent}1a`, color: accent } : undefined}>
                        <input type="radio" name={`pregunta-${p.id}`} style={{ accentColor: accent }}
                          checked={sel}
                          onChange={() => setRespuestas((r) => ({ ...r, [p.id]: { opcionId: o.id } }))} />
                        {o.texto}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        <button onClick={enviar} disabled={enviando} style={{ backgroundColor: accent }}
          className="w-full py-3 rounded-lg hover:opacity-90 text-white font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2">
          {enviando && <Loader2 className="size-4 animate-spin" />}
          Enviar respuestas
        </button>
      </div>
    </div>
  );
}
